import { localizedConferenceName } from "./conference-names";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import type {
  ConferenceDashboardClient,
  PortfolioConferenceSummary,
  SponsorshipAgreement,
} from "./portfolio-overview-api";
import type { UserRole } from "./auth-types";
import { portfolioErrorMessageKey } from "./portfolio-api-errors";
import { OtherRevenuePanel } from "./OtherRevenuePanel";
import { RevenueField } from "./RevenueField";
import { RiyalAmount, RiyalSymbol } from "./RiyalAmount";
import {
  FINANCIAL_REFERENCE_PATTERN,
  localDateTimeInputValue,
  updateRevenueDrafts,
} from "./revenue-form-state";
import { useRevenueDraft, type RevenueDraftReporter } from "./useRevenueDraft";
import {
  bidiIsolationAttributes,
  isolateText,
  useLocale,
  type MessageKey,
} from "./localization";

function parseMinorUnits(value: string): number | null {
  const match = value.trim().match(/^(\d+)(?:\.(\d{1,2}))?$/);
  if (!match?.[1]) return null;
  const major = Number(match[1]);
  const fraction = Number((match[2] ?? "").padEnd(2, "0"));
  const amount = major * 100 + fraction;
  return Number.isSafeInteger(amount) && amount > 0 ? amount : null;
}

function timestampFromLocal(value: string): string | null {
  const time = Date.parse(value);
  return Number.isFinite(time) ? new Date(time).toISOString() : null;
}

function MovementForm({
  agreement,
  kind,
  client,
  onSaved,
  onDraftChange,
}: {
  readonly agreement: SponsorshipAgreement;
  readonly kind: "invoice" | "payment";
  readonly client: ConferenceDashboardClient;
  readonly onSaved: (agreement: SponsorshipAgreement) => void;
  readonly onDraftChange: RevenueDraftReporter;
}) {
  const { t } = useLocale();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [evidenceAttachmentId, setEvidenceAttachmentId] = useState("");
  const [initialTime, setInitialTime] = useState(localDateTimeInputValue);
  const [occurredAt, setOccurredAt] = useState(initialTime);
  const [method, setMethod] = useState<
    "bank_transfer" | "bank_deposit" | "other"
  >("bank_transfer");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<MessageKey | null>(null);
  const prefix = `${kind}-${agreement.sponsorshipId}`;
  useRevenueDraft(
    prefix,
    busy ||
      Boolean(
        amount ||
        reference ||
        evidenceAttachmentId ||
        occurredAt !== initialTime ||
        method !== "bank_transfer",
      ),
    onDraftChange,
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amountMinor = parseMinorUnits(amount);
    const timestamp = timestampFromLocal(occurredAt);
    if (!amountMinor || !timestamp) {
      setMessage("sponsorship.form.invalidMovement");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const updated =
        kind === "invoice"
          ? await client.recordSponsorshipInvoice(agreement.sponsorshipId, {
              amountMinor,
              currency: agreement.currency,
              reference,
              evidenceAttachmentId,
              issuedAt: timestamp,
            })
          : await client.recordSponsorshipPayment(agreement.sponsorshipId, {
              amountMinor,
              currency: agreement.currency,
              method,
              reference,
              evidenceAttachmentId,
              receivedAt: timestamp,
            });
      onSaved(updated);
      setAmount("");
      setReference("");
      setEvidenceAttachmentId("");
      const nextTime = localDateTimeInputValue();
      setInitialTime(nextTime);
      setOccurredAt(nextTime);
      setMethod("bank_transfer");
      setMessage(
        kind === "invoice"
          ? "sponsorship.form.invoiceRecorded"
          : "sponsorship.form.paymentRecorded",
      );
    } catch (error: unknown) {
      setMessage(
        portfolioErrorMessageKey(
          error,
          kind === "invoice"
            ? "sponsorship.form.invoiceFailed"
            : "sponsorship.form.paymentFailed",
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="revenue-action" ref={detailsRef}>
      <summary>
        {kind === "invoice"
          ? t("sponsorship.form.recordInvoice")
          : t("sponsorship.form.recordPayment")}
      </summary>
      <form
        className="revenue-form revenue-form--compact"
        onSubmit={submit}
        autoComplete="off"
        aria-busy={busy}
      >
        <p className="revenue-form__intro">{t("finance.form.requiredHint")}</p>
        <RevenueField
          id={`${prefix}-amount`}
          label={
            <>
              {t("sponsorship.form.amountLabel")}{" "}
              {agreement.currency === "SAR" ? (
                <RiyalSymbol />
              ) : (
                <bdi {...bidiIsolationAttributes()}>{agreement.currency}</bdi>
              )}
            </>
          }
        >
          <input
            id={`${prefix}-amount`}
            disabled={busy}
            name="amount"
            inputMode="decimal"
            placeholder={t("finance.placeholder.amount")}
            {...bidiIsolationAttributes()}
            required
            value={amount}
            onChange={(event) => setAmount(event.currentTarget.value)}
          />
        </RevenueField>
        <RevenueField
          id={`${prefix}-reference`}
          label={t(
            kind === "invoice"
              ? "sponsorship.form.invoiceReference"
              : "sponsorship.form.paymentReference",
          )}
          hint={t(
            kind === "invoice"
              ? "sponsorship.form.invoiceReferenceHint"
              : "finance.field.referenceHint",
          )}
        >
          <input
            id={`${prefix}-reference`}
            disabled={busy}
            name="reference"
            pattern={FINANCIAL_REFERENCE_PATTERN}
            title={t("finance.field.referenceInvalid")}
            aria-describedby={`${prefix}-reference-hint`}
            spellCheck={false}
            required
            minLength={2}
            maxLength={160}
            {...bidiIsolationAttributes()}
            value={reference}
            onChange={(event) => setReference(event.currentTarget.value)}
          />
        </RevenueField>
        <RevenueField
          id={`${prefix}-evidence`}
          label={t("finance.field.evidence")}
          hint={t("finance.field.evidenceHint")}
        >
          <input
            id={`${prefix}-evidence`}
            disabled={busy}
            name="evidenceAttachmentId"
            pattern={FINANCIAL_REFERENCE_PATTERN}
            title={t("finance.field.referenceInvalid")}
            aria-describedby={`${prefix}-evidence-hint`}
            spellCheck={false}
            required
            minLength={2}
            maxLength={160}
            {...bidiIsolationAttributes()}
            value={evidenceAttachmentId}
            onChange={(event) =>
              setEvidenceAttachmentId(event.currentTarget.value)
            }
          />
        </RevenueField>
        {kind === "payment" ? (
          <RevenueField
            id={`${prefix}-method`}
            label={t("sponsorship.form.paymentMethod")}
          >
            <select
              id={`${prefix}-method`}
              disabled={busy}
              name="method"
              value={method}
              onChange={(event) =>
                setMethod(
                  event.currentTarget.value as
                    "bank_transfer" | "bank_deposit" | "other",
                )
              }
            >
              <option value="bank_transfer">
                {t("finance.method.bankTransfer")}
              </option>
              <option value="bank_deposit">
                {t("finance.method.bankDeposit")}
              </option>
              <option value="other">{t("finance.method.other")}</option>
            </select>
          </RevenueField>
        ) : null}
        <RevenueField
          id={`${prefix}-date`}
          label={
            kind === "invoice"
              ? t("sponsorship.form.issuedAt")
              : t("sponsorship.form.receivedAt")
          }
          hint={t("finance.field.localTimeHint")}
        >
          <input
            id={`${prefix}-date`}
            disabled={busy}
            name="occurredAt"
            aria-describedby={`${prefix}-date-hint`}
            type="datetime-local"
            {...bidiIsolationAttributes()}
            required
            value={occurredAt}
            onChange={(event) => setOccurredAt(event.currentTarget.value)}
          />
        </RevenueField>
        {message ? (
          <p className="form-message" role="status">
            {t(message)}
          </p>
        ) : null}
        <div className="revenue-form__actions">
          <button
            className="button button--primary"
            type="submit"
            disabled={busy}
          >
            {busy
              ? t("finance.action.saving")
              : kind === "invoice"
                ? t("sponsorship.form.saveInvoice")
                : t("sponsorship.form.savePayment")}
          </button>
          <button
            className="button button--quiet"
            type="button"
            disabled={busy}
            onClick={() => {
              setAmount("");
              setReference("");
              setEvidenceAttachmentId("");
              setMethod("bank_transfer");
              const nextTime = localDateTimeInputValue();
              setInitialTime(nextTime);
              setOccurredAt(nextTime);
              setMessage(null);
              if (detailsRef.current) {
                detailsRef.current.open = false;
                detailsRef.current.querySelector("summary")?.focus();
              }
            }}
          >
            {t("finance.form.cancel")}
          </button>
        </div>
      </form>
    </details>
  );
}

function AgreementForm({
  conferenceId,
  client,
  onCreated,
  onDraftChange,
}: {
  readonly conferenceId: string;
  readonly client: ConferenceDashboardClient;
  readonly onCreated: (agreement: SponsorshipAgreement) => void;
  readonly onDraftChange: RevenueDraftReporter;
}) {
  const { t } = useLocale();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [organization, setOrganization] = useState("");
  const [packageName, setPackageName] = useState("");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [evidenceAttachmentId, setEvidenceAttachmentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<MessageKey | null>(null);
  useRevenueDraft(
    `sponsorship-${conferenceId}`,
    busy ||
      Boolean(
        organization ||
        packageName ||
        amount ||
        reference ||
        evidenceAttachmentId,
      ),
    onDraftChange,
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const contractedAmountMinor = parseMinorUnits(amount);
    if (!contractedAmountMinor) {
      setMessage("sponsorship.form.invalidContract");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const created = await client.createSponsorship({
        conferenceId,
        sponsorOrganization: organization,
        packageName,
        currency: "SAR",
        contractedAmountMinor,
        agreementReference: reference,
        evidenceAttachmentId,
      });
      onCreated(created);
      setOrganization("");
      setPackageName("");
      setAmount("");
      setReference("");
      setEvidenceAttachmentId("");
      setMessage("sponsorship.form.created");
    } catch (error: unknown) {
      setMessage(
        portfolioErrorMessageKey(error, "sponsorship.form.createFailed"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="revenue-create" ref={detailsRef}>
      <summary className="button button--primary">
        {t("sponsorship.form.add")}
      </summary>
      <form
        className="revenue-form"
        onSubmit={submit}
        autoComplete="off"
        aria-busy={busy}
      >
        <div className="revenue-form__heading">
          <h2>{t("sponsorship.form.detailsHeading")}</h2>
          <p>{t("finance.form.requiredHint")}</p>
        </div>
        <RevenueField
          id="sponsor-organization"
          label={t("sponsorship.form.organization")}
        >
          <input
            id="sponsor-organization"
            disabled={busy}
            name="organization"
            required
            minLength={2}
            maxLength={200}
            value={organization}
            onChange={(event) => setOrganization(event.currentTarget.value)}
          />
        </RevenueField>
        <RevenueField
          id="sponsor-package"
          label={t("sponsorship.form.package")}
        >
          <input
            id="sponsor-package"
            disabled={busy}
            name="packageName"
            required
            minLength={2}
            maxLength={120}
            placeholder={t("sponsorship.form.packagePlaceholder")}
            value={packageName}
            onChange={(event) => setPackageName(event.currentTarget.value)}
          />
        </RevenueField>
        <RevenueField
          id="sponsor-value"
          label={
            <>
              {t("sponsorship.form.valueLabel")} <RiyalSymbol />
            </>
          }
        >
          <input
            id="sponsor-value"
            disabled={busy}
            name="amount"
            inputMode="decimal"
            required
            placeholder={t("finance.placeholder.amount")}
            {...bidiIsolationAttributes()}
            value={amount}
            onChange={(event) => setAmount(event.currentTarget.value)}
          />
        </RevenueField>
        <h3 className="revenue-form__section">
          {t("finance.form.referencesHeading")}
        </h3>
        <RevenueField
          id="sponsor-reference"
          label={t("sponsorship.form.agreementReference")}
          hint={t("sponsorship.form.referenceHint")}
        >
          <input
            id="sponsor-reference"
            disabled={busy}
            name="agreementReference"
            pattern={FINANCIAL_REFERENCE_PATTERN}
            title={t("finance.field.referenceInvalid")}
            aria-describedby="sponsor-reference-hint"
            spellCheck={false}
            required
            minLength={2}
            maxLength={160}
            {...bidiIsolationAttributes()}
            value={reference}
            onChange={(event) => setReference(event.currentTarget.value)}
          />
        </RevenueField>
        <RevenueField
          id="sponsor-evidence"
          label={t("finance.field.evidence")}
          hint={t("finance.field.evidenceHint")}
        >
          <input
            id="sponsor-evidence"
            disabled={busy}
            name="evidenceAttachmentId"
            pattern={FINANCIAL_REFERENCE_PATTERN}
            title={t("finance.field.referenceInvalid")}
            aria-describedby="sponsor-evidence-hint"
            spellCheck={false}
            required
            minLength={2}
            maxLength={160}
            {...bidiIsolationAttributes()}
            value={evidenceAttachmentId}
            onChange={(event) =>
              setEvidenceAttachmentId(event.currentTarget.value)
            }
          />
        </RevenueField>
        {message ? (
          <p className="form-message" role="status">
            {t(message)}
          </p>
        ) : null}
        <div className="revenue-form__actions">
          <button
            className="button button--primary"
            type="submit"
            disabled={busy}
          >
            {busy
              ? t("sponsorship.form.creating")
              : t("sponsorship.form.create")}
          </button>
          <button
            className="button button--quiet"
            type="button"
            disabled={busy}
            onClick={() => {
              setOrganization("");
              setPackageName("");
              setAmount("");
              setReference("");
              setEvidenceAttachmentId("");
              setMessage(null);
              if (detailsRef.current) {
                detailsRef.current.open = false;
                detailsRef.current.querySelector("summary")?.focus();
              }
            }}
          >
            {t("finance.form.cancel")}
          </button>
        </div>
      </form>
    </details>
  );
}

export function SponsorshipsPanel({
  client,
  role,
}: {
  readonly client: ConferenceDashboardClient;
  readonly role: UserRole;
}) {
  const { locale, t } = useLocale();
  const [drafts, setDrafts] = useState<ReadonlySet<string>>(() => new Set());
  const reportDraft = useCallback((id: string, dirty: boolean) => {
    setDrafts((current) => updateRevenueDrafts(current, id, dirty));
  }, []);
  const [conferences, setConferences] = useState<
    readonly PortfolioConferenceSummary[]
  >([]);
  const [conferenceId, setConferenceId] = useState("");
  const [agreements, setAgreements] = useState<readonly SponsorshipAgreement[]>(
    [],
  );
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState<MessageKey>(
    "sponsorship.unavailable.description",
  );

  useEffect(() => {
    const controller = new AbortController();
    void client
      .load(controller.signal)
      .then((overview) => {
        setConferences(overview.conferences);
        setConferenceId(
          (current) => current || overview.conferences[0]?.conferenceId || "",
        );
        if (overview.conferences.length === 0) setStatus("ready");
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setErrorMessage(
            portfolioErrorMessageKey(
              error,
              "sponsorship.unavailable.description",
            ),
          );
          setStatus("error");
        }
      });
    return () => controller.abort();
  }, [client]);

  const loadAgreements = useCallback(
    (selectedConferenceId: string, signal?: AbortSignal) => {
      if (!selectedConferenceId) return Promise.resolve();
      setStatus("loading");
      return client
        .loadSponsorships(selectedConferenceId, signal)
        .then((records) => {
          setAgreements(records);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (!(error instanceof DOMException && error.name === "AbortError")) {
            setErrorMessage(
              portfolioErrorMessageKey(
                error,
                "sponsorship.unavailable.description",
              ),
            );
            setStatus("error");
          }
        });
    },
    [client],
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadAgreements(conferenceId, controller.signal);
    return () => controller.abort();
  }, [conferenceId, loadAgreements]);

  const totals = useMemo(
    () =>
      agreements.reduce(
        (result, agreement) => ({
          contracted: result.contracted + agreement.contractedAmountMinor,
          invoiced: result.invoiced + agreement.invoicedAmountMinor,
          collected: result.collected + agreement.collectedAmountMinor,
          outstanding: result.outstanding + agreement.outstandingAmountMinor,
        }),
        { contracted: 0, invoiced: 0, collected: 0, outstanding: 0 },
      ),
    [agreements],
  );
  const replaceAgreement = (updated: SponsorshipAgreement) =>
    setAgreements((current) =>
      current.map((agreement) =>
        agreement.sponsorshipId === updated.sponsorshipId ? updated : agreement,
      ),
    );
  const canManage = role === "admin";

  if (status === "error") {
    return (
      <section className="overview-state" role="alert">
        <h2>{t("sponsorship.unavailable.heading")}</h2>
        <p>{t(errorMessage)}</p>
        <button
          className="button button--secondary"
          type="button"
          onClick={() => void loadAgreements(conferenceId)}
        >
          {t("finance.action.tryAgain")}
        </button>
      </section>
    );
  }

  return (
    <section
      className="revenue-workspace"
      aria-label={t("sponsorship.management.aria")}
    >
      <div className="revenue-toolbar">
        <label htmlFor="revenue-conference">
          {t("sponsorship.conference")}
        </label>
        <select
          id="revenue-conference"
          disabled={drafts.size > 0}
          aria-describedby={
            drafts.size > 0 ? "revenue-context-hint" : undefined
          }
          value={conferenceId}
          onChange={(event) => setConferenceId(event.currentTarget.value)}
        >
          {conferences.map((conference) => (
            <option
              key={conference.conferenceId}
              value={conference.conferenceId}
            >
              {isolateText(localizedConferenceName(conference, locale))}
            </option>
          ))}
        </select>
      </div>
      {drafts.size > 0 ? (
        <p
          id="revenue-context-hint"
          className="revenue-context-hint"
          role="status"
        >
          {t("finance.form.contextLocked")}
        </p>
      ) : null}
      {conferenceId && canManage ? (
        <AgreementForm
          key={`sponsorship-${conferenceId}`}
          conferenceId={conferenceId}
          client={client}
          onDraftChange={reportDraft}
          onCreated={(created) =>
            setAgreements((current) => [...current, created])
          }
        />
      ) : null}

      <div
        className="revenue-summary"
        aria-label={t("sponsorship.totals.aria")}
      >
        {(
          [
            ["sponsorship.metric.contracted", totals.contracted],
            ["sponsorship.metric.invoiced", totals.invoiced],
            ["sponsorship.metric.collected", totals.collected],
            ["sponsorship.metric.outstanding", totals.outstanding],
          ] as const
        ).map(([messageKey, value]) => (
          <article key={messageKey}>
            <span>{t(messageKey)}</span>
            <strong>
              <RiyalAmount minor={value} fractionDigits={2} />
            </strong>
          </article>
        ))}
      </div>

      {status === "loading" ? (
        <section className="overview-state" aria-live="polite">
          <h2>{t("sponsorship.loading")}</h2>
        </section>
      ) : agreements.length === 0 ? (
        <section className="overview-state">
          <h2>{t("sponsorship.empty.heading")}</h2>
          <p>{t("sponsorship.empty.description")}</p>
        </section>
      ) : (
        <div className="sponsorship-list">
          {agreements.map((agreement) => (
            <article className="sponsorship-card" key={agreement.sponsorshipId}>
              <header>
                <div>
                  <span>
                    <bdi>{agreement.packageName}</bdi>
                  </span>
                  <h2>
                    <bdi>{agreement.sponsorOrganization}</bdi>
                  </h2>
                </div>
                <code {...bidiIsolationAttributes()}>
                  {agreement.agreementReference}
                </code>
              </header>
              <dl>
                <div>
                  <dt>{t("sponsorship.metric.contracted")}</dt>
                  <dd>
                    <RiyalAmount
                      minor={agreement.contractedAmountMinor}
                      currency={agreement.currency}
                      fractionDigits={2}
                    />
                  </dd>
                </div>
                <div>
                  <dt>{t("sponsorship.metric.invoiced")}</dt>
                  <dd>
                    <RiyalAmount
                      minor={agreement.invoicedAmountMinor}
                      currency={agreement.currency}
                      fractionDigits={2}
                    />
                  </dd>
                </div>
                <div>
                  <dt>{t("sponsorship.metric.collected")}</dt>
                  <dd>
                    <RiyalAmount
                      minor={agreement.collectedAmountMinor}
                      currency={agreement.currency}
                      fractionDigits={2}
                    />
                  </dd>
                </div>
                <div>
                  <dt>{t("sponsorship.metric.outstanding")}</dt>
                  <dd>
                    <RiyalAmount
                      minor={agreement.outstandingAmountMinor}
                      currency={agreement.currency}
                      fractionDigits={2}
                    />
                  </dd>
                </div>
              </dl>
              <p className="sponsorship-card__evidence">
                {t("sponsorship.evidence")}{" "}
                <code {...bidiIsolationAttributes()}>
                  {agreement.evidenceAttachmentId}
                </code>
              </p>
              {canManage ? (
                <div className="sponsorship-card__actions">
                  <MovementForm
                    agreement={agreement}
                    kind="invoice"
                    client={client}
                    onSaved={replaceAgreement}
                    onDraftChange={reportDraft}
                  />
                  <MovementForm
                    agreement={agreement}
                    kind="payment"
                    client={client}
                    onSaved={replaceAgreement}
                    onDraftChange={reportDraft}
                  />
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
      <OtherRevenuePanel
        key={`other-revenue-${conferenceId}`}
        conferenceId={conferenceId}
        client={client}
        canManage={canManage}
        onDraftChange={reportDraft}
      />
    </section>
  );
}
